import { type FC, memo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { ButtonKind, ButtonVariant } from "@stubhub/broadway-web";
import { EventGridListItemFooter, useFormattedEventDateTime } from "@stubhub/ensemble-discovery";
import type { ShowtimeItem } from "src/http/types";
import { useContent } from "src/shared/components/content";
import { ResidencyShowtimeButton } from "src/website-apps/responsive/stubhub-performer/components/calendar/residency/ResidencyShowtimeButton";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";

interface Props {
  showtimes: readonly ShowtimeItem[];
  onSoldOutClick?: (item: ShowtimeItem) => void;
  onShowtimeClick?: () => void;
  onShowtimeInView?: (index: number) => void;
}

const rowClass = css({
  display: "flex",
  columnGap: "primitives.100",
  rowGap: "primitives.100",
  flexWrap: "nowrap",
  overflowX: "auto",
});

const footerClass = css({
  paddingBlockEnd: "primitives.0",
});

const chipWrapperClass = css({ flexShrink: 0 });

const ShowtimeChip: FC<{
  item: ShowtimeItem;
  index: number;
  onSoldOutClick?: ((item: ShowtimeItem) => void) | undefined;
  onShowtimeClick?: (() => void) | undefined;
  onShowtimeInView?: ((index: number) => void) | undefined;
}> = memo(({ item, index, onSoldOutClick, onShowtimeClick, onShowtimeInView }) => {
  const tbaLabel = useContent("cid$#4099720#"); // TBA
  const { formattedTime } = useFormattedEventDateTime(
    item.eventStartDateTime,
    undefined,
    item.venueTimeZoneOffset,
    undefined,
    undefined,
  );

  const isTba = item.isTbd || !formattedTime;
  const isSoldOut = !isTba && !item.hasActiveListings;

  return (
    <div className={chipWrapperClass}>
      {onShowtimeInView !== undefined && <InViewTracker onInView={() => onShowtimeInView(index)} />}
      <ResidencyShowtimeButton
        time={isTba ? (tbaLabel ?? "") : (formattedTime ?? "")}
        isTba={isTba}
        isSoldOut={isSoldOut}
        {...(isSoldOut || isTba ? {} : { href: item.url ?? "#", ...(onShowtimeClick && { onClick: onShowtimeClick }) })}
        {...(onSoldOutClick === undefined ? {} : { onSoldOutClick: () => onSoldOutClick(item) })}
        showNotificationIcon
        kind={ButtonKind.Primary}
        variant={isSoldOut || isTba ? ButtonVariant.Regular : ButtonVariant.Outline}
      />
    </div>
  );
});

ShowtimeChip.displayName = "ShowtimeChip";

export const ShowtimeChips: FC<Props> = memo(({ showtimes, onSoldOutClick, onShowtimeClick, onShowtimeInView }) => (
  <EventGridListItemFooter classes={{ container: footerClass }}>
    <div className={rowClass}>
      {showtimes.map((item, idx) => (
        <ShowtimeChip
          key={item.eventId}
          item={item}
          index={idx}
          onSoldOutClick={onSoldOutClick}
          {...(onShowtimeClick ? { onShowtimeClick } : {})}
          {...(onShowtimeInView ? { onShowtimeInView } : {})}
        />
      ))}
    </div>
  </EventGridListItemFooter>
));

ShowtimeChips.displayName = "ShowtimeChips";
